import React, {type ReactNode} from 'react';

import {
  type InfiniteListFetcherHookProps,
  useInfiniteListFetcherRTK,
} from './hooks/useInfiniteListFetcherRTK';

/**
 * Everything {@link useInfiniteListFetcherRTK} returns except the
 * `subscriptions` node, which the wrapper renders on your behalf.
 */
export type InfiniteListRTKRenderProps<IT extends {id: number | string}> = {
  results: Array<IT>;
  loadNext: () => void;
  hasNext: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  totalCount: number;
  refetch: () => Promise<unknown>;
};

export type InfiniteListRTKProps<
  IT extends {id: number | string},
  FP extends {page: string}
> = InfiniteListFetcherHookProps<IT, FP> & {
  children: (list: InfiniteListRTKRenderProps<IT>) => ReactNode;
};

/**
 * Self-contained wrapper around {@link useInfiniteListFetcherRTK}. It renders
 * the hook's `subscriptions` node for you, so callers never have to remember
 * to — they just read the list state from the render prop:
 *
 *   <InfiniteListRTK
 *     fetchParams={{objectId: id}}
 *     useDataHook={useThings}
 *     paramsConverter={toThingParams}
 *   >
 *     {({results, loadNext, hasNext, isLoading}) => ...rest UI...}
 *   </InfiniteListRTK>
 *
 * Every loaded page stays a live RTK Query subscription, so invalidating the
 * query's tags or calling `refetch()` refreshes the whole list at once.
 *
 * Prefer this over calling the hook directly unless you specifically need to
 * place the subscriptions node yourself.
 */
export function InfiniteListRTK<
  IT extends {id: number | string},
  FP extends {page: string}
>({children, ...hookProps}: InfiniteListRTKProps<IT, FP>) {
  const {subscriptions, ...list} = useInfiniteListFetcherRTK<IT, FP>(hookProps);

  return (
    <>
      {subscriptions}
      {children(list)}
    </>
  );
}
